import { Fragment, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { AcuityDistance } from '@optiflow/types';
import { cn } from '@/utils/cn';
import {
  ACUITY_ROWS,
  fieldPath,
  normaliseAcuity,
  type AcuityEye,
  type AcuityForm,
  type AcuityKey,
  type FormErrors,
} from '../examinationForm';

const EYES: { eye: AcuityEye; abbreviation: 'OD' | 'OS' | 'OU' }[] = [
  { eye: 'right', abbreviation: 'OD' },
  { eye: 'left', abbreviation: 'OS' },
  { eye: 'binocular', abbreviation: 'OU' },
];

interface AcuityGridProps {
  value: Record<AcuityKey, AcuityForm>;
  /** Absent: the grid is read-only. */
  onChange?: (next: Record<AcuityKey, AcuityForm>) => void;
  errors?: FormErrors;
}

/**
 * Visual acuity, distance then near, through no correction, the current one and the new refraction, for OD, OS and OU.
 *
 * Each value is kept as written in its notation — nothing is converted or compared. The grid reads left to right in every language.
 */
export function AcuityGrid({ value, onChange, errors = {} }: AcuityGridProps) {
  const { t } = useTranslation();
  const baseId = useId();
  const readOnly = !onChange;

  const messages = ACUITY_ROWS.flatMap((row) =>
    EYES.flatMap(({ eye, abbreviation }) => {
      const error = errors[fieldPath.acuity(row.key, eye)];
      return error
        ? [{ id: `${baseId}-${row.key}-${eye}-error`, row, eye, abbreviation, error }]
        : [];
    }),
  );
  const messageId = (key: AcuityKey, eye: AcuityEye) =>
    messages.find((message) => message.row.key === key && message.eye === eye)?.id;

  const update = (key: AcuityKey, eye: AcuityEye, next: string) => {
    if (!onChange) return;
    onChange({ ...value, [key]: { ...value[key], [eye]: next } });
  };

  return (
    <div>
      <div dir="ltr" className="overflow-x-auto">
        <table className="w-full min-w-[34rem] border-separate border-spacing-x-2 border-spacing-y-1.5 text-sm">
          <caption className="sr-only">{t('medical.sections.acuity')}</caption>
          <thead>
            <tr>
              <th scope="col" className="text-muted text-start text-xs font-semibold" dir="auto">
                {t('medical.acuity.test')}
              </th>
              {EYES.map(({ abbreviation }) => (
                <th
                  key={abbreviation}
                  scope="col"
                  className="text-muted text-start text-xs font-semibold"
                >
                  <abbr title={t(`medical.eyes.${abbreviation}`)} className="no-underline">
                    {abbreviation}
                  </abbr>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[AcuityDistance.DISTANCE, AcuityDistance.NEAR].map((distance) => (
              <Fragment key={distance}>
                <tr>
                  <th
                    scope="rowgroup"
                    colSpan={4}
                    className="text-ink pt-2 text-start text-xs font-semibold uppercase"
                    dir="auto"
                  >
                    {t(`medical.acuity.distances.${distance}`)}
                  </th>
                </tr>
                {ACUITY_ROWS.filter((row) => row.distance === distance).map((row) => (
                  <tr key={row.key}>
                    <th scope="row" className="text-muted text-start font-normal" dir="auto">
                      {t(`medical.acuity.corrections.${row.correction}`)}
                    </th>
                    {EYES.map(({ eye, abbreviation }) => {
                      const current = value[row.key][eye];
                      if (readOnly) {
                        return (
                          <td key={eye} className="text-ink font-mono tabular-nums">
                            {current || '—'}
                          </td>
                        );
                      }
                      const invalid = Boolean(messageId(row.key, eye));
                      return (
                        <td key={eye}>
                          <input
                            aria-label={t('medical.acuity.cell', {
                              distance: t(`medical.acuity.distances.${row.distance}`),
                              correction: t(`medical.acuity.corrections.${row.correction}`),
                              eye: abbreviation,
                            })}
                            aria-invalid={invalid || undefined}
                            aria-describedby={messageId(row.key, eye)}
                            autoComplete="off"
                            spellCheck={false}
                            value={current}
                            maxLength={16}
                            onChange={(event) => update(row.key, eye, event.target.value)}
                            onBlur={() => {
                              const tidy = normaliseAcuity(current);
                              if (tidy !== current) update(row.key, eye, tidy);
                            }}
                            className={cn(
                              'border-line w-full min-w-0 rounded-lg border bg-white px-2 py-2 font-mono text-sm tabular-nums',
                              'focus:border-brand-500 focus:ring-brand-100 focus:ring-2 focus:outline-none',
                              invalid && 'border-red-400 focus:border-red-500 focus:ring-red-100',
                            )}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {messages.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {messages.map((message) => (
            <li key={message.id} id={message.id} className="text-sm text-red-600">
              <bdi>
                {t(`medical.acuity.distances.${message.row.distance}`)} ·{' '}
                {t(`medical.acuity.corrections.${message.row.correction}`)}
              </bdi>{' '}
              <span dir="ltr" className="font-mono">
                {message.abbreviation}
              </span>
              {': '}
              {t(message.error.key, message.error.values)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
